import type { ProactiveRule } from "@go-novus/admin-api";

/** 模板里唯一支持的占位符；i18n 文案通过 {placeholder} 参数拿到这个字面量。 */
export const PRODUCT_TITLE_TOKEN = "{product_title}";

/**
 * 草稿里的规则和契约同形，只有等待秒数允许为 null：
 * null 表示商家清空了输入框，按必填字段校验拦下保存。
 */
export interface ProactiveRuleDraft extends Omit<ProactiveRule, "waitSeconds"> {
  waitSeconds: number | null;
}

/** 无交互默认等待 4 秒，元素可见与进店默认等待 2 秒。 */
export function defaultWaitSeconds(trigger: ProactiveRule["trigger"]): number {
  return trigger === "product_idle" ? 4 : 2;
}

/** 新规则默认停用、默认商品页无交互（GON-62）。 */
export function createProactiveRule(id: string): ProactiveRuleDraft {
  return {
    enabled: false,
    id,
    prompt: "",
    question: "",
    selector: "",
    trigger: "product_idle",
    waitSeconds: defaultWaitSeconds("product_idle"),
  };
}

/** 元素规则必须先有目标，才能启用；无目标草稿仍可保存和拾取。 */
export function canEnableProactiveRule(rule: ProactiveRuleDraft): boolean {
  return rule.trigger !== "element_visible" || rule.selector.trim() !== "";
}

/** 所有草稿编辑共用同一约束，切换类型或清空目标时同步停用。 */
export function updateProactiveRule(
  rule: ProactiveRuleDraft,
  changes: Partial<ProactiveRuleDraft>,
): ProactiveRuleDraft {
  const next = { ...rule, ...changes };
  return { ...next, enabled: next.enabled && canEnableProactiveRule(next) };
}

/**
 * 切换触发类型时套用类型默认值并清掉不适用的字段：
 * 等待秒数回到该类型初始值；离开元素规则时清空目标，元素目标为空时停用。
 */
export function switchProactiveRuleTrigger(
  rule: ProactiveRuleDraft,
  trigger: ProactiveRule["trigger"],
): ProactiveRuleDraft {
  return updateProactiveRule(rule, {
    ...(trigger !== "element_visible" ? { selector: "" } : {}),
    trigger,
    waitSeconds: defaultWaitSeconds(trigger),
  });
}

/** 在提示文案末尾补上 {product_title}；前面有字就先空一格。 */
export function appendProductTitle(prompt: string): string {
  const needsSpace = prompt.length > 0 && !prompt.endsWith(" ");
  return `${prompt}${needsSpace ? " " : ""}${PRODUCT_TITLE_TOKEN}`;
}
